'use client'

import { useQuery } from '@tanstack/react-query'
import { createClient } from '@/shared/lib/supabase/client'
import { useUser } from './useUser'

export const UNREAD_FEEDBACK_QUERY_KEY = ['feedback', 'unread'] as const

export interface UnreadTicket {
  id: string
  subject: string | null
  app_id: string | null
  feedback_type: string
  status: string
  latest_admin_body: string
  latest_admin_at: string
}

/**
 * The user's support tickets with an admin reply they haven't opened yet,
 * newest reply first — drives the badge on the account avatar and the
 * "support replied" toast. Marking a ticket read (MarkTicketRead on the
 * detail page) invalidates this key.
 */
export function useUnreadTickets(): UnreadTicket[] {
  const { data: user } = useUser()

  const { data } = useQuery({
    // Scoped by user id so a sign-in/sign-out never serves one account's
    // unread list to another. Invalidation by the unscoped prefix still hits.
    queryKey: [...UNREAD_FEEDBACK_QUERY_KEY, user?.id],
    enabled: !!user,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const supabase = createClient()
      const { data, error } = await supabase.rpc('unread_feedback_tickets')
      if (error) throw error
      return (data as UnreadTicket[]) ?? []
    },
  })

  return data ?? []
}

/** Number of unread tickets — the count on the avatar badge. */
export function useUnreadFeedback(): number {
  return useUnreadTickets().length
}
